import type { DoctorsListOrder } from "@hermes/api";
import { Flag } from "@hermes/flags";
import { useMessage } from "@hermes/intl";
import { searchParamTypes as t, useSearchParams } from "@hermes/shared";
import {
  ButtonV2,
  cssFns,
  usePrincipalColors,
  useScreenDetector,
} from "@hermes/ui";

import { useAppConfig } from "#internal/app/config/context";

// import { ChildrenAgeFilter } from "./children-age";
import { DateFilter } from "./date";
import { InsurancesFilter } from "./insurances";
import { LanguageFilter } from "./language";
import { Sort } from "./sort";
import type { DoctorsListParams } from "./types";

export const useParams = () => {
  const [params, setParams] = useSearchParams(
    {
      sort: t.string<DoctorsListOrder | undefined>({
        enum: ["rating", "price", "experience"],
      }),
      nearme: t.boolean(),
      date: t.plainDate(),
      language: t.string({}),
      insurance: t.string({}),
      child_age: t.number<number>(),
    },
    { allowUnknown: true },
  );

  return [
    {
      nearme: params.nearme,
      sort: params.nearme
        ? "distance"
        : (params.sort as DoctorsListOrder | undefined),
      date: params.date,
      language: params.language,
      insurance: params.insurance,
      child_age: params.child_age,
    },
    setParams,
  ] as const;
};

export const DoctorListFilters = ({
  params,
  onParamsChange,
  enableChildFilter,
  isChild,
  onChildChange,
  variant,
}: {
  params: DoctorsListParams;
  onParamsChange: (params: DoctorsListParams) => void;
  enableChildFilter?: boolean;
  isChild?: boolean;
  onChildChange?: () => void;
  variant?: "filter-search" | "filter-outlined";
}) => {
  const principalColors = usePrincipalColors();
  const { isMobile } = useScreenDetector();
  const message = useMessage();
  const { insurances } = useAppConfig();

  return (
    <div
      css={[
        isMobile && { backgroundColor: principalColors.ebblue },
        {
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 10,
        },
      ]}
    >
      <div
        css={[
          isMobile && [
            cssFns.padding(10),
            {
              paddingInlineEnd: 0,
            },
          ],
          {
            display: "flex",
            alignItems: "center",
            columnGap: isMobile ? "6px" : "16px",
            overflowX: "auto",
            scrollbarWidth: "none",
          },
        ]}
      >
        <Sort
          params={params}
          onParamsChange={onParamsChange}
          variant={variant}
        />
        {enableChildFilter && (
          <Flag
            id="1a7d4b25-6711-4f32-8118-ba3a5556d167"
            description="Enable children filter in doctors list page"
          >
            <ButtonV2
              text={message({
                id: "doctors-list.filters.children",
                default: "Детский",
              })}
              variant={
                variant
                  ? variant
                  : isMobile
                    ? "filter-search"
                    : "filter-outlined"
              }
              active={isChild}
              onPress={onChildChange}
            />
          </Flag>
        )}
        <Flag
          id="635e3e5e-50c6-47f6-b2d3-fdf24547c652"
          description="Enable near filter in doctors list page"
        >
          <ButtonV2
            text={message({
              id: "doctors-list.filters.near",
              default: "Рядом",
            })}
            variant={
              variant ? variant : isMobile ? "filter-search" : "filter-outlined"
            }
            active={params.nearme}
            onPress={() => {
              onParamsChange({
                nearme: !params.nearme,
                sort: undefined,
              });
            }}
          />
        </Flag>
        <Flag
          id="ff5b8251-73f3-43b0-91c6-205bd5d4b3ba"
          description="Enable date filter in doctors list page"
        >
          <DateFilter
            params={params}
            onParamsChange={onParamsChange}
            variant={variant}
          />
        </Flag>

        <Flag id="language-filter">
          <LanguageFilter
            params={params}
            onParamsChange={onParamsChange}
            variant={variant}
          />
        </Flag>
        {insurances.length > 0 && (
          <InsurancesFilter
            params={params}
            onParamsChange={onParamsChange}
            variant={variant}
          />
        )}
      </div>
      {/* {isChild && (
        <ChildrenAgeFilter
          params={params}
          onParamsChange={onParamsChange}
          variant={variant}
        />
      )} */}
    </div>
  );
};
